import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// mode: 'fixed' (no selector, always fixedDialCode) or 'dynamic' (show the
// full country selector) - set per LoB, see vms/src/utils/lob.js#getCountryCodeConfigForLob.
export interface CountryCodeConfig {
  mode: 'fixed' | 'dynamic';
  fixedDialCode?: string;
  // Pre-selected dial code of a dynamic LoB (e.g. '+60' for Malaysia).
  defaultDialCode?: string;
}

// Display currency of the voucher's LoB (vms getVoucherBySaralCode).
export interface CurrencyConfig {
  code: string;
  symbol: string;
  locale: string;
}

// Local timezone of the voucher's LoB (vms getVoucherBySaralCode).
export interface TimezoneConfig {
  code: string;
  iana: string;
  label: string;
}

export interface VoucherResponse {
  token: string;
  roleResourceId: number | null;
  expiryDate: string;
  receiverPhone?: string;
  country_code: CountryCodeConfig;
  display_currency: CurrencyConfig;
  display_timezone?: TimezoneConfig;
  programId: number;
  saralId: string;
  final_balance: number;
  activated: boolean;
  // True while the bank is still processing issuance (e.g. ICICI CBDC's
  // Single Disbursement was queued but Transaction Check Status hasn't
  // confirmed a terminal outcome yet). Never true for eRupi, which is
  // always resolved synchronously.
  pending: boolean;
  // Normalized processor brand from the backend (e.g. "ICICI", "ICICI_CBDC").
  // Use this to branch copy/instructions - not to be confused with the
  // program's raw processorType string, which this already normalizes.
  brand: string | null;
  transactions: Array<{
    created_at: string;
    amount: string;
    tenant_name: string;
    tenant_id: string;
  }>;
  program: {
    logo: string;
    brandImage?: string;
    displayName: string;
    description: string;
    receiverHowToUseInstructions: string;
    standardTerms: string;
    voucherImage: string;
    beneficiaryDetails?: any;
    programId: number;
  };
}

export interface UserDetails {
  number: string;
  name: string;
}

export interface ActivationResult extends UserDetails {
  bankLogo: string | null;
  // The processor's own expiry for the voucher just issued (ISO string), when it
  // reports one (MYP does) - more exact than the pre-activation expiryDate.
  expiryDate?: string | null;
  // "success" (issuance confirmed) or "pending" (bank is still processing
  // - e.g. ICICI CBDC's bounded status-check window didn't resolve in
  // time; the user will be notified once it does). Never "pending" for
  // eRupi flows.
  status: 'success' | 'pending';
  responseMessage: string | null;
}
export interface TenantItem {
  tenantId: number;
  tenantName?: string;
  tenantBrand?: string;
  floor?: string | null;
  storeLogo?: string | null;
}

// Async thunks
export const fetchVoucher = createAsyncThunk<
  VoucherResponse,
  { saral_code: string; pin: string },
  { rejectValue: string }
>(
  'voucher/fetchVoucher',
  async ({ saral_code, pin }, { rejectWithValue }) => {
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/api/v1/vouchers/fetchVoucherDetails`,
        timeout: 10000000,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          saralcode: saral_code,
          pin: pin
        },
        method: 'POST'
      });

      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.message || 'Failed to fetch voucher details');
      } else {
        return rejectWithValue('Something went wrong');
      }
    }
  }
);

export const createVoucher = createAsyncThunk<
  ActivationResult,
  { saralId: string; user_details: UserDetails; token: string },
  { rejectValue: string }
>(
  'voucher/createVoucher',
  async ({ saralId, user_details, token }, { rejectWithValue }) => {
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/api/v1/vouchers/activate`,
        timeout: 10000000,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        data: {
          saralId,
          user_details
        },
        method: 'POST'
      });
      // "pending" is not a failure - the bank is still processing (e.g.
      // ICICI CBDC's bounded status-check window didn't resolve in time).
      // Only treat anything other than success/pending as a rejection.
      if (response.data.status === "success" || response.data.status === "pending") {
        return {
          ...user_details,
          bankLogo: response.data.bankLogo || null,
          expiryDate: response.data.expiryDate || null,
          status: response.data.status,
          responseMessage: response.data.responseMessage || null,
        };
      } else {
        return rejectWithValue(response.data.responseMessage || response.data.response?.message || 'Failed to activate voucher');
      }
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.message || 'Failed to activate voucher');
      }
      return rejectWithValue(error.message || 'Failed to activate voucher');
    }
  }
);

export const clearStore = createAsyncThunk(
  'voucher/clearStore',
  async () => {
    // This action doesn't need to do anything async, but we keep it for consistency
    return null;
  }
); 

// Fetch tenants for Locations accordion
export const fetchTenants = createAsyncThunk<
  TenantItem[],
  { programId: number; roleResourceId: number; token: string },
  { rejectValue: string }
>(
  'voucher/fetchTenants',
  async ({ programId, roleResourceId, token }, { rejectWithValue }) => {
    try {
      const response = await axios.request({
        baseURL: `${window.env.API_URL}/v1/programs/tenant/${roleResourceId}`,
        timeout: 10000000,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        method: 'GET',
        params: {
          programId,
          limit: 1000
        }
      });

      const tenants: TenantItem[] = response?.data?.tenants || [];
      return tenants;
    } catch (error: any) {
      if(error.response) {
        return rejectWithValue(error.response.data.message || 'Failed to load tenants');
      } else {
        return rejectWithValue('Something went wrong');
      }
    }
  }
);